/**
 * Short notices in the corner: "Saved", "Added 8 songs to the queue".
 * Anything that needs a decision is a dialog instead; a toast never asks.
 */

import { useSyncExternalStore } from "react";
import { Icon } from "./Icons";

export interface ToastAction {
  label: string;
  run: () => void | Promise<void>;
}

interface Notice {
  id: number;
  text: string;
  tone: "ok" | "bad" | "info";
  action?: ToastAction;
}

let notices: Notice[] = [];
let seq = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((fn) => fn());

/** A short notice. With an `action` (Undo) it stays a little longer, so there is time to press it. */
export function toast(text: string, tone: Notice["tone"] = "info", ms = 4000, action?: ToastAction) {
  noticeWithId(text, tone, ms, action);
}

/** As `toast`, returning the notice's id so it can be taken down early (`dismissToast`). */
export function noticeWithId(text: string, tone: Notice["tone"] = "info", ms = 4000, action?: ToastAction): number {
  const id = ++seq;
  notices = [...notices.slice(-3), { id, text, tone, action }];
  emit();
  const wait = action ? Math.max(ms, 9000) : tone === "bad" ? ms * 2 : ms;
  window.setTimeout(() => dismiss(id), wait);
  return id;
}

/** Take a notice down before its time (its offer no longer applies). */
export const dismissToast = (id: number) => dismiss(id);

function dismiss(id: number) {
  notices = notices.filter((n) => n.id !== id);
  emit();
}

export function Toasts() {
  const list = useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => notices
  );
  return (
    <div className="nv-toasts" role="status" aria-live="polite">
      {list.map((n) => (
        <div key={n.id} className={`nv-toast ${n.tone}`}>
          <span>{n.text}</span>
          {n.action && (
            <button className="nv-toast-action" onClick={() => { dismiss(n.id); void n.action?.run(); }}>
              {n.action.label}
            </button>
          )}
          <button className="nv-iconbtn sm" onClick={() => dismiss(n.id)} aria-label="Dismiss">
            <Icon name="close" size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
